import { useCallback, useEffect, useState } from "react";
import { useManagerScope } from "@/components/ManagerLayout";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

function colorFor(count) {
  if (!count) return "bg-gray-50 border-gray-100";
  if (count === 1) return "bg-[#F5EDD0]";
  if (count === 2) return "bg-[#C9A84C]/80 text-[#1A2744]";
  if (count === 3) return "bg-[#C9A84C] text-[#1A2744]";
  return "bg-[#1A2744] text-white";
}

function fmtFullDate(iso) {
  return new Date(iso + "T00:00:00").toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

export default function ManagerLeaveHeatmap() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [includePending, setIncludePending] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const { scopeToken } = useManagerScope();

  const load = useCallback(() => {
    api.get("/manager/leave/heatmap", { params: { year, include_pending: includePending } })
      .then(({ data }) => setData(data)).catch(() => {});
  }, [year, includePending]);
  useEffect(() => { load(); }, [load, scopeToken]);

  if (!data) return <div className="text-sm text-gray-500">Loading heatmap…</div>;

  const byDate = Object.fromEntries(data.days.map((d) => [d.date, d]));
  const holidayByDate = {};
  for (const h of data.holidays) holidayByDate[h.date] = h;

  // Build 12-month grid
  const months = Array.from({ length: 12 }).map((_, m) => {
    const first = new Date(year, m, 1);
    const last = new Date(year, m + 1, 0);
    const days = [];
    for (let d = 1; d <= last.getDate(); d++) {
      const dateIso = new Date(year, m, d).toISOString().slice(0, 10);
      days.push(dateIso);
    }
    return { name: MONTHS[m], first, days };
  });

  return (
    <div className="space-y-6" data-testid="mgr-heatmap-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.25em] font-semibold">Leave heatmap</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A2744] mt-1">{year} · {data.site === "SITE_1" ? "Christchurch" : "Wellington"}</h1>
          <p className="text-sm text-gray-500 mt-1">Cell intensity = staff on leave that day. Public holidays shown as a gold bar on top.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => setYear(year - 1)}>← {year - 1}</Button>
          <span className="text-sm font-medium px-2">{year}</span>
          <Button variant="outline" size="sm" onClick={() => setYear(year + 1)}>{year + 1} →</Button>
          <label className="ml-4 text-xs text-gray-600 flex items-center gap-1.5 cursor-pointer">
            <input
              type="checkbox"
              checked={includePending}
              onChange={(e) => setIncludePending(e.target.checked)}
              data-testid="toggle-include-pending"
            />
            Include pending
          </label>
        </div>
      </header>

      {/* Legend */}
      <div className="flex items-center gap-3 text-[11px] text-gray-600 flex-wrap">
        <span>0</span>
        <div className="w-6 h-4 rounded bg-gray-50 border border-gray-200"></div>
        <span>1</span>
        <div className="w-6 h-4 rounded bg-[#F5EDD0]"></div>
        <span>2</span>
        <div className="w-6 h-4 rounded bg-[#C9A84C]/80"></div>
        <span>3</span>
        <div className="w-6 h-4 rounded bg-[#C9A84C]"></div>
        <span>4+</span>
        <div className="w-6 h-4 rounded bg-[#1A2744]"></div>
        <span className="ml-4">Public holiday</span>
        <div className="w-6 h-4 rounded bg-emerald-500/90"></div>
      </div>

      <Card>
        <CardContent className="p-4 overflow-x-auto">
          <div className="min-w-[700px] space-y-3">
            {months.map((m) => (
              <div key={m.name} className="flex items-center gap-3">
                <div className="w-10 text-xs font-semibold text-[#1A2744]">{m.name}</div>
                <div className="flex gap-0.5 flex-wrap flex-1">
                  {m.days.map((d) => {
                    const entry = byDate[d];
                    const hol = holidayByDate[d];
                    const count = entry?.count || 0;
                    return (
                      <button
                        key={d}
                        onClick={() => setSelected(d)}
                        className={`relative h-5 w-5 rounded-sm border border-white/40 ${colorFor(count)} hover:ring-2 hover:ring-[#C9A84C]`}
                        title={`${d}${hol ? ` · ${hol.name}` : ""}${count ? ` · ${count} off` : ""}`}
                        data-testid={`heatmap-day-${d}`}
                      >
                        {hol && <span className="absolute inset-x-0 top-0 h-1 bg-emerald-500/90 rounded-t-sm"></span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Day detail */}
      {selected && (() => {
        const entry = byDate[selected];
        const hol = holidayByDate[selected];
        const count = entry?.count || 0;
        let staffingTone = "emerald";
        if (count >= 4) staffingTone = "red";
        else if (count >= 2) staffingTone = "amber";
        return (
          <Card className="border-[#C9A84C]/40" data-testid="heatmap-day-detail">
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#C9A84C] font-semibold">Day detail</div>
                  <h3 className="font-heading text-xl font-bold text-[#1A2744]">{fmtFullDate(selected)}</h3>
                  {hol && <div className="mt-1 text-sm text-emerald-700 font-medium">🎉 {hol.name}{hol.scope !== "NATIONAL" ? " (regional)" : ""}</div>}
                </div>
                <Button size="sm" variant="outline" onClick={() => setSelected(null)}>Close</Button>
              </div>
              <div className="mt-4">
                <div className="text-xs text-gray-500">Staff on leave</div>
                <div className="mt-1">
                  {count === 0 ? (
                    <p className="text-sm text-emerald-700">✅ No one is on leave.</p>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {entry.entries.map((e, i) => (
                        <li key={e.id || `${e.employee_name}-${i}`} className="py-2 text-sm flex items-center justify-between">
                          <span className="font-medium text-[#1A2744]">{e.employee_name}</span>
                          <span className="text-xs text-gray-500">{e.leave_type} · {e.status}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={`mt-3 text-sm font-medium ${
                  staffingTone === "red" ? "text-red-600" :
                  staffingTone === "amber" ? "text-amber-700" : "text-emerald-700"
                }`}>
                  {staffingTone === "red" ? "❌ Heavy leave — plan cover carefully." :
                   staffingTone === "amber" ? "⚠ Tight staffing — check roster." :
                   "✅ Adequate coverage available."}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}
    </div>
  );
}
